import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { AppShell } from '@/components/layout/AppShell';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import Dashboard from '@/pages/Dashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import CategoriesPage from '@/pages/CategoriesPage';
import LawsPage from '@/pages/LawsPage';
import LawWizard from '@/pages/LawWizard';
import LawDetail from '@/pages/LawDetail';
import LawEdit from '@/pages/LawEdit';
import ScansPage from '@/pages/ScansPage';
import SettingsPage from '@/pages/SettingsPage';
import RunScan from '@/pages/RunScan';
import ScanResults from '@/pages/ScanResults';
import BillingPage from '@/pages/BillingPage';
import FinesPage from '@/pages/FinesPage';
import GenerateFinePage from '@/pages/GenerateFinePage';
import PublicScoresPage from '@/pages/PublicScoresPage';
import PublicScorePage from '@/pages/PublicScorePage';
import LandingPage from '@/pages/LandingPage';
import NotFoundPage from '@/pages/NotFoundPage';
import { TranslationProvider } from '@/i18n/TranslationContext';
import { ThemeProvider } from '@/lib/theme-context';
import '@/App.css';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Admin route wrapper - requires admin or super_admin role
function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Legal Entry route wrapper - requires legal_entry or super_admin role
function LegalRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'legal_entry' && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Public route (redirect to dashboard if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Placeholder pages
function PlaceholderPage({ title, description }) {
  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>
      <div className="flex items-center justify-center min-h-[400px] rounded-lg border border-dashed border-border/60 bg-muted/10">
        <p className="text-sm text-muted-foreground">Coming soon</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/scans" element={<ScansPage />} />
        <Route path="/scans/run" element={<RunScan />} />
        <Route path="/scans/:scanId" element={<ScanResults />} />
        <Route path="/laws" element={<LegalRoute><LawsPage /></LegalRoute>} />
        <Route path="/laws/new" element={<LegalRoute><LawWizard /></LegalRoute>} />
        <Route path="/laws/:lawId/edit" element={<LegalRoute><LawEdit /></LegalRoute>} />
        <Route path="/laws/:lawId" element={<LegalRoute><LawDetail /></LegalRoute>} />
        <Route path="/categories" element={<LegalRoute><CategoriesPage /></LegalRoute>} />
        <Route path="/subscriptions" element={<BillingPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/fines" element={<FinesPage />} />
        <Route path="/fines/generate/:scanId" element={<GenerateFinePage />} />
        <Route path="/public-scores" element={<PublicScoresPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="/public/score/:entityId" element={<PublicScorePage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <TranslationProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                className: 'border-border bg-card text-foreground',
              }}
            />
          </AuthProvider>
        </TranslationProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
